import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { fetchRunSpans, type ApiSpan } from '../../lib/api'
import { Page } from '../../components/layout/AppShell'
import { PageHeader } from '../../components/layout/PageHeader'
import { Badge, Panel, Spinner, ErrorState, Json } from '../../components/ui'
import { useActiveWorkspace } from '../../lib/workspace'

export function RunDetailPage() {
  const { runId } = useParams({ from: '/app/agents/runs/$runId' })
  const navigate = useNavigate()
  const { activeWorkspaceKey } = useActiveWorkspace()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['run-spans', activeWorkspaceKey, runId],
    queryFn: () => fetchRunSpans(activeWorkspaceKey, runId),
    enabled: !!activeWorkspaceKey,
  })

  const runsCrumb = { label: 'Agent runs', onClick: () => navigate({ to: '/agents', search: { tab: 'runs' } }) }

  const childrenByParent = useMemo(() => {
    const map = new Map<string | null, ApiSpan[]>()
    for (const s of data?.spans ?? []) {
      const key = s.parent_span_id
      const arr = map.get(key) ?? []
      arr.push(s)
      map.set(key, arr)
    }
    return map
  }, [data])

  if (isLoading) {
    return (
      <Page width="wide" header={<PageHeader crumbs={[runsCrumb, { label: 'Run' }]} />}>
        <Spinner />
      </Page>
    )
  }
  if (isError || !data) {
    return (
      <Page width="wide" header={<PageHeader crumbs={[runsCrumb, { label: 'Run' }]} />}>
        <ErrorState />
      </Page>
    )
  }

  const { run, spans } = data
  const roots = childrenByParent.get(null) ?? []

  return (
    <Page
      width="wide"
      header={
        <PageHeader
          crumbs={[runsCrumb, { label: run.agent_name }]}
          actions={
            <div className="flex items-center gap-2">
              {run.thesis_id && (
                <button
                  type="button"
                  onClick={() => navigate({ to: '/theses/$thesisId', params: { thesisId: run.thesis_id! } })}
                  className="text-sm text-indigo-600 hover:underline"
                >
                  View thesis
                </button>
              )}
              <Badge tone={run.status === 'failed' ? 'red' : 'neutral'}>{run.status}</Badge>
            </div>
          }
        />
      }
    >
      <div className="flex flex-col gap-3">
        {run.error && (
          <Panel title="Error">
            <p className="text-sm text-red-600">{run.error}</p>
          </Panel>
        )}
        {spans.length === 0 ? (
          <Panel title="Spans">
            <p className="px-1 py-6 text-center text-sm text-text-muted">No spans recorded.</p>
          </Panel>
        ) : (
          roots.map((s) => <SpanNode key={s.span_id} span={s} childrenByParent={childrenByParent} depth={0} />)
        )}
      </div>
    </Page>
  )
}

function SpanNode({
  span,
  childrenByParent,
  depth,
}: {
  span: ApiSpan
  childrenByParent: Map<string | null, ApiSpan[]>
  depth: number
}) {
  const [open, setOpen] = useState(true)
  const kids = childrenByParent.get(span.span_id) ?? []
  const hasKids = kids.length > 0

  return (
    <div style={{ marginLeft: depth > 0 ? 16 : 0 }}>
      <Panel
        title={
          <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left">
            {hasKids ? (open ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="w-[14px]" />}
            <span className="text-text-muted">#{span.sequence_id}</span>
            <Badge tone={span.is_error ? 'red' : 'indigo'}>{span.kind}</Badge>
            {span.name}
            {hasKids && <span className="text-xs text-text-muted">({kids.length})</span>}
          </button>
        }
      >
        {open && (
          <div className="flex flex-col gap-3">
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-text-muted">Input</p>
              <Json value={span.input} />
            </div>
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-text-muted">Output</p>
              <Json value={span.output} />
            </div>
            {span.error && <p className="text-sm text-red-600">{span.error}</p>}
          </div>
        )}
      </Panel>
      {open && hasKids && (
        <div className="mt-3 flex flex-col gap-3">
          {kids.map((k) => (
            <SpanNode key={k.span_id} span={k} childrenByParent={childrenByParent} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}
